import { Injectable } from '@angular/core';
import {
    ActivatedRouteSnapshot,
    CanActivate,
    Router,
    RouterStateSnapshot,
    UrlTree,
} from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { PostLoginRedirectService } from 'app/core/app-shell/post-login-redirect.service';

/**
 * NoWorkspaceGuard — gates /workspace-pending (SPEC-web-planner-013 AC-4; it
 * gated /setup-wizard before Phase 1) against users who already have a
 * workspace. Sibling to NoAuthGuard. SESSION-meet-ux-004e (GAP-MEETUX-024B).
 * It only admits or sends away: the zero-tenant redirect to the holding page
 * lives in sign-in.component.ts, and a redirect added here would loop.
 *
 * Three cases:
 *   1. Authenticated user with `user.tenants.length === 0` → activate.
 *   2. Authenticated user with 1+ tenants → redirect via PostLoginRedirect
 *      (admin → /admin, fs → /deals, is → /queue, cro → /portfolio) and block.
 *   3. No Firebase user → bounce to /sign-in and block.
 *
 * The parent route already enforces `AuthGuard`, so case 3 is the defensive
 * net for a token that has expired between AuthGuard and this guard.
 */
@Injectable({ providedIn: 'root' })
export class NoWorkspaceGuard implements CanActivate {
    constructor(
        private _authService: AuthService,
        private _router: Router,
        private _postLoginRedirect: PostLoginRedirectService
    ) {}

    canActivate(
        _route: ActivatedRouteSnapshot,
        _state: RouterStateSnapshot
    ): boolean | UrlTree {
        if (!this._authService.firebaseUser) {
            this._router.navigateByUrl('/sign-in');
            return false;
        }

        const tenants = this._authService.user?.tenants ?? [];
        if (tenants.length === 0) {
            return true;
        }

        // User already has at least one tenant — route them to their persona
        // home. PostLoginRedirectService is the single owner of post-login
        // navigation (SESSION-002); do not duplicate the persona resolution
        // here. Pass undefined for `selectedTenant` (typed `TenantCompactDto`)
        // and let the resolver pick the active tenant from `user.tenants` —
        // `authService.selectedTenant` is the larger TenantDto and would be a
        // type-narrowing miss here.
        this._postLoginRedirect.redirectAfterLogin(
            this._authService.user,
            undefined
        );
        return false;
    }
}
